import { appURL } from '../constants/APIconstants';
import { ROUTES, INTERNAL_ROUTING } from '../constants/routes.js';

const { CHECK_IN_CODE } = INTERNAL_ROUTING.QUERY_PARAMS;
const TOTP_CODE_PATTERN = /^\d{6}$/;
const APP_HOST = new URL(appURL).host;

const CHECK_IN_PATH_PATTERN = new RegExp(
  `^${ROUTES.EDUCATIONAL_EVENT_CHECK_IN.replace(':id', '([^/]+)')}$`,
);

const parseUrl = url => {
  try {
    return new URL(url, appURL);
  } catch {
    return null;
  }
};

const checkInPathname = ({ pathname }) => pathname.replace(/\/$/, '');

/**
 * Builds the absolute URL encoded in the host's check-in QR.
 *
 * The URL points at the env-aware web app domain (`appURL`) so that a native
 * phone camera surfaces a tappable "Open" action, and so the OS's universal-link
 * matching hands it to the app instead of the browser. The path/query shape is
 * the same one `LINKING_MAPPER.educationalEventCheckIn` parses back out.
 *
 * `appURL` already encodes the environment (localhost / staging / production),
 * keeping the QR domain in lockstep with the deep-link matcher per build — a
 * staging build emits a staging URL, which only the staging app will claim.
 *
 * A rotating QR carries the current 6-digit code; a static QR omits `code`
 * entirely. Throws on a missing eventId or a provided-but-non-6-digit code so a
 * malformed-but-valid-looking URL can never be encoded into a QR.
 *
 * @param {{ eventId: string, code?: string }} params
 * @returns {string} e.g. https://staging.app.c8health.com/events/abc123/check-in?code=482910
 */
export const buildCheckInUrl = ({ eventId, code }) => {
  const hasCode = code !== undefined;
  if (!eventId || (hasCode && !TOTP_CODE_PATTERN.test(code))) {
    throw new Error('buildCheckInUrl requires a non-empty eventId and, if given, a 6-digit code');
  }
  const url = parseUrl(ROUTES.EDUCATIONAL_EVENT_CHECK_IN.replace(':id', eventId));
  if (hasCode) url.searchParams.set(CHECK_IN_CODE, code);
  return url.toString();
};

/**
 * Parses a scanned/deep-link payload into a check-in, or null if the payload
 * isn't a valid educational-event check-in URL on our domain.
 *
 * A URL without a `code` param is a static QR and yields `code: undefined`; a
 * `code` param that is present must be a well-formed 6-digit code, otherwise the
 * result is null. Used by the in-app scanner to ignore unrelated QRs and by the
 * deep-link mapper to route the native-camera URL.
 *
 * @param {string} data raw barcode / URL payload
 * @returns {{ eventId: string, code: string | undefined } | null}
 */
export const extractCheckIn = data => {
  const url = parseUrl(data);
  if (!url || url.host !== APP_HOST) return null;

  const [, eventId] = checkInPathname(url).match(CHECK_IN_PATH_PATTERN) ?? [];
  if (!eventId) return null;

  if (!url.searchParams.has(CHECK_IN_CODE)) return { eventId, code: undefined };

  const code = url.searchParams.get(CHECK_IN_CODE);
  if (!TOTP_CODE_PATTERN.test(code)) return null;

  return { eventId, code };
};
